import React, { useContext, useMemo, useState } from 'react';
import { ProductMasterData } from '../../../../../../types/product_master_data/productMasterData';
import { ProductContext } from '../../../../../../context/ProductContext';
import { useNavigate } from 'react-router-dom';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { Product } from '../../../../../../types/product';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { LotContext } from '../../../../../../context/LotContext';
import { BinContext } from '../../../../../../context/BinContext';
import QuickActionModal from './QuickActionModal';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { BinStatusContext } from '../../../../../../context/BinStatusContext';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import { TagRelationContext } from '../../../../../../context/TagRelationContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { StockLocationRole } from '../../../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import SearchBarWithFilter from '../../../../../../VentoryUI/components/common/SearchBarWithFilter';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { ProductFilter } from '../../../../../../VentoryUI/components/filters/ProductFilter';
import { Routing } from '../../../../../../util/routes.util';
import { CreateButton } from '../../../../../components/Button';
import { productDetailTableHeaders } from '../../../../../components/Table/Product/ProductDetailTable';
import Table from '../../../../../components/Table';
import { ProductDetailTableSettings } from '../../../../../../types/product_table_settings/productDetailTableSettings';
import { ProductFilterType } from '../../../../../../VentoryUI/components/filters/ProductFilter/ProductFilter';
import { KitContext } from '../../../../../../context/KitContext';
import { KitTemplateContext } from '../../../../../../context/KitTemplateContext';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { TableSettingsContext } from '../../../../../../context/TableSettingsContext';
import { EntityType } from '../../../../../../types/comment';
import { UserContext } from '../../../../../../context/UserContext';
import { ContainerTypeContext } from '../../../../../../context/ContainerTypeContext';

interface ProductInfoPaneInputProps {
  productMasterData?: ProductMasterData;
  footer: () => JSX.Element;
}

export default function ProductInfoPane({ productMasterData: pmd, footer }: ProductInfoPaneInputProps) {
  const navigate = useNavigate();

  const { currentCompany, companies } = useContext(CompanyContext);
  const { products, productsLoading } = useContext(ProductContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { lots } = useContext(LotContext);
  const { bins } = useContext(BinContext);
  const { hasStockLocationRoleInCompany } = useContext(StockLocationRoleAssignmentContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { containers } = useContext(ContainerContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { customFields } = useContext(CustomFieldContext);
  const { handlingUnitConnections } = useContext(HandlingUnitContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { kits } = useContext(KitContext);
  const { kitTemplates } = useContext(KitTemplateContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { locale } = useContext(UserContext);

  const productTableSettings = getTableSettings(EntityType.product, currentCompany.id);
  const containerTableSettings = getTableSettings(EntityType.container, currentCompany.id);

  const [quickActionModalOpen, setQuickActionModalOpen] = useState<boolean>(false);
  const [product, setProduct] = useState<Product | undefined>();

  if (!pmd) return null; // TODO: Entity not found

  const allProducts = useMemo(() => {
    const items: Product[] = [...products.values()].flatMap(itemMap => Array.from(itemMap.values()));

    const hus = Array.from(handlingUnits.values());
    const actualProducts: Product[] = [];
    for (const item of items) {
      const pmd = productMasterData.get(item.productMasterDataId);
      if (!pmd) continue;
      if (pmd.handlingUnit) {
        const hu = hus.find(hu => hu.parentId === pmd.id);
        if (!hu) {
          actualProducts.push(item);
        } else if (hu.default) {
          actualProducts.push(item);
        } else {
          actualProducts.push(Product.forHandlingUnit(item, hu));
        }
        continue;
      }

      actualProducts.push(item);
    }

    return actualProducts.filter(i => i.productMasterDataId === pmd.id);
  }, [products, productMasterData, handlingUnits]);

  const productsByIds = useMemo(() => {
    return new Map(
      Array.from(products.values())
        .flatMap(i => [...i.values()])
        .map(i => [i.id, i]),
    );
  }, [handlingUnits, productMasterData, products]);

  const allItems = useMemo(() => {
    return allProducts.map(i => productsByIds.get(i.id)).filter(i => i) as Product[];
  }, [allProducts]);

  const [items, setItems] = useState<Product[]>(allItems);

  const itemProducts = new Set(items.map(i => i.id));
  const quantity = allProducts
    .filter(i => itemProducts.has(i.id))
    .map(i => BigInt(i.unitQuantity))
    .reduce((a, b) => a + b, BigInt(0));

  const isSupervisor = hasStockLocationRoleInCompany(currentCompany.id, StockLocationRole.STOCK_LOCATION_SUPERVISOR);

  const handleClick = (item: Product) => {
    setQuickActionModalOpen(true);
    setProduct(item);
  };

  const headers = productDetailTableHeaders(
    allItems,
    products,
    stockLocations,
    bins,
    containers,
    productMasterData,
    lots,
    customFields,
    handlingUnitConnections,
    kits,
    kitTemplates,
    new ProductDetailTableSettings({ companyId: currentCompany.id }),
    companies,
    currentCompany,
    locale,
    pmd.id,
  );

  const filter = new ProductFilter(
    stockLocations,
    productMasterData,
    bins,
    binStatuses,
    containers,
    containerTypes,
    tagRelations,
    customFields,
    new Map(allItems.map(i => [i.id, i])),
    productTableSettings,
    containerTableSettings,
    ProductFilterType.detail,
    currentCompany,
  );

  return (
    <>
      {quickActionModalOpen && (
        <QuickActionModal
          open={!!quickActionModalOpen}
          setOpen={c => {
            setQuickActionModalOpen(c);
            setProduct(undefined);
          }}
          pmdId={product?.productMasterDataId || pmd.id}
          productId={product?.id}
          props={{}}
        />
      )}

      <div className='flex flex-grow flex-col gap-4'>
        <div className='grid grid-cols-3'>
          <div>
            <p className='font-semibold'>{t(TranslationKey.productName).singular.label}</p>
            <p
              className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
              onClick={() => navigate(Routing.productMasterData.update(pmd.id).info())}
              style={{ lineClamp: 2 }}
            >
              {pmd.productName}
            </p>
          </div>
          <div className='justify-self-center'>
            <p className='font-semibold'>{t(TranslationKey.productNumber).singular.label}</p>
            <p
              className='mt-2 line-clamp-2 w-fit cursor-pointer text-xl font-bold text-gray-400'
              onClick={() => navigate(Routing.productMasterData.update(pmd.id).info())}
            >
              {pmd.productNumber}
            </p>
          </div>
          <div className='justify-self-end'>
            <p className='font-semibold'>{t(TranslationKey.quantity).singular.label}</p>
            <p className='mt-2 text-xl font-bold text-gray-400'>
              {`${UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, quantity.toString() || '0')}`}
            </p>
          </div>
        </div>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={productsLoading || productMasterDataLoading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.filterProducts).singular.label}
            filter={filter}
          />
          <div className='flex'>
            {isSupervisor ? <CreateButton onClick={() => setQuickActionModalOpen(true)} /> : null}
          </div>
        </div>
        <Table
          loading={!items.length && (productsLoading || productMasterDataLoading)}
          items={items}
          disabled={item => item.productMasterDataId !== pmd.id}
          disabledOnClick={handleClick}
          totalItemCount={allItems.length}
          headers={headers}
          onClick={handleClick}
        />
      </div>
    </>
  );
}
